import { Fragment, FunctionComponent, memo, useMemo } from "react";
import {
    Legend,
    XAxis,
    YAxis,
    Line,
    ResponsiveContainer,
    Tooltip,
    CartesianGrid,
    ComposedChart,
    Area,
    ReferenceArea,
    ReferenceLine,
} from "recharts";
import { isBefore } from "date-fns";

import MetricValuesChartTooltip from "./metric-values-chart-tooltip";
import { formatChartData, getXAxisData } from "./metric-values-chart.utilities";
import { formatDateTickValue } from "utilities/format-date-tick-value";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { IProps } from "./metric-values-chart.types";
import "./metric-values-chart.scss";
import { isFilterSingleSelection } from "utilities/is-filter-single-selection";

/**
 * Chart predicted and actual values of a metric over time.
 * @param props.endDateTime        The end date of the anomaly being displayed.
 * @param props.filterRange        A tuple describing the range for the date filter applied to the chart.
 * @param props.height             Optional height of the chart. Mostly for testing.
 * @param props.isPreview          Whether to show axis lines or not.
 * @param props.metricModelOutputs Array of actual and predicted metric values over time.
 * @param props.mouseCallbacks     Event callbacks for mousedown, mousemove and mouseup
 * @param props.syncId             Recharts property to syncronise the tooltips of multiple charts.
 * @param props.width              Optional width of the chart. Mostly for testing.
 * @returns                        Line chart with actual and predicated values.
 */
const MetricValuesChart: FunctionComponent<IProps> = ({
    endDateTime,
    filterRange,
    height = "100%",
    isPreview = false,
    metricModelOutputs,
    mouseCallbacks,
    startDateTime,
    syncId,
    width = "100%",
}) => {
    const chartData = useMemo(() => formatChartData(metricModelOutputs), [metricModelOutputs]);
    const [xAxisTicks, xAxisInterval] = useMemo(() => getXAxisData(chartData), [chartData]);

    // Use a single line for showing the selected area when a click is made on chart
    const isSingleSelection = isFilterSingleSelection(filterRange);

    return (
        <ResponsiveContainer className="metric-values-chart" height={height} width={width}>
            <ComposedChart data={chartData} syncId={syncId} {...mouseCallbacks}>
                {!isPreview && (
                    <XAxis
                        dataKey="time"
                        domain={[xAxisTicks[0], xAxisTicks[xAxisTicks.length - 1]]}
                        interval={xAxisInterval}
                        tick={{ fontSize: 10 }}
                        tickCount={xAxisTicks.length}
                        tickFormatter={(time) => formatDateTickValue(time, TimePeriodGroupBy.Hours)}
                        ticks={xAxisTicks}
                        type="number"
                        xAxisId={0}
                    />
                )}

                {!isPreview && (
                    <XAxis
                        axisLine={false}
                        xAxisId={1}
                        dataKey="dateTime"
                        scale="band"
                        interval={0}
                        tickLine={false}
                        tick={({ x, y, payload }) => {
                            const { offset, value } = payload;

                            // Display label in the middle of each day.
                            if (chartData[payload.index].isMiddleOfDay) {
                                return (
                                    <text x={x} y={y} textAnchor="middle">
                                        {formatDateTickValue(value, TimePeriodGroupBy.Days)}
                                    </text>
                                );
                            }

                            // Vertical line to separate days.
                            if (chartData[payload.index].isLastOfDay) {
                                const pathX = Math.floor(x + offset) + 0.5;
                                return <path d={`M${pathX},${y + 3}v-15`} />;
                            }

                            return <Fragment />;
                        }}
                    />
                )}
                <YAxis
                    allowDecimals={false}
                    axisLine={!isPreview}
                    domain={["dataMin", "auto"]}
                    tick={!isPreview}
                    tickFormatter={(value) => Math.round(value).toString()}
                    width={isPreview ? 0 : undefined}
                />

                <Line
                    className="metric-values-chart__line--actual"
                    dataKey="actualValue"
                    dot={false}
                    stroke="#248bcf"
                    strokeWidth={1.75}
                />

                <Area stroke="none" fill="#3182BD" fillOpacity={0.3} dataKey="confidenceRange" />

                {/* Highlight the area of the anomaly. */}
                <ReferenceArea
                    stroke={"grey"}
                    fillOpacity={0.1}
                    x1={startDateTime.getTime()}
                    x2={
                        isBefore(endDateTime, chartData[chartData.length - 1].dateTime)
                            ? endDateTime.getTime()
                            : chartData[chartData.length - 1].dateTime.getTime()
                    }
                />

                {isSingleSelection ? (
                    <ReferenceLine
                        isFront
                        stroke={"#1abc9c"}
                        strokeOpacity={1}
                        strokeWidth={2}
                        x={filterRange?.[0].activeLabel ?? ""}
                    />
                ) : (
                    <ReferenceArea
                        fill={"#1abc9c"}
                        fillOpacity={0.3}
                        x1={filterRange?.[0].activeLabel ?? ""}
                        x2={filterRange?.[1].activeLabel ?? ""}
                    />
                )}

                <CartesianGrid vertical={false} strokeDasharray="5 5" />

                {!isPreview && (
                    <Legend
                        align="right"
                        formatter={(label) => {
                            switch (label) {
                                case "actualValue":
                                    return "Actual";
                                case "confidenceRange":
                                    return "Expected";
                                case "anomalyPeriod":
                                    return "Anomaly Period";
                            }
                        }}
                        wrapperStyle={{ top: "-20px" }}
                        verticalAlign="top"
                        iconType={"square"}
                        payload={[
                            { type: "line", value: "actualValue", color: "#248bcf" },
                            { type: "rect", value: "confidenceRange", color: "#3183bd86" },
                            { type: "rect", value: "anomalyPeriod", color: "#a6a6a6" },
                        ]}
                    />
                )}

                <Tooltip
                    content={({ active, payload }) =>
                        active &&
                        Array.isArray(payload) &&
                        payload.length > 0 &&
                        typeof payload[0].value === "number" && (
                            <MetricValuesChartTooltip
                                actualValue={payload[0].payload.actualValue}
                                dateTime={payload[0].payload.dateTime}
                                predictedLower={payload[0].payload.confidenceRange[0]}
                                predictedUpper={payload[0].payload.confidenceRange[1]}
                            />
                        )
                    }
                    isAnimationActive={false}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default memo(MetricValuesChart);
